import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { usePageMeta } from "@/lib/meta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function UnsubscribePage() {
  usePageMeta({
    title: "Unsubscribe | Peanut Valet",
    description: "Manage your Peanut Valet update email subscription.",
    canonical: "https://peanutvalet.com/unsubscribe",
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setState("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    setState("submitting");
    try {
      const res = await axios.post(`${API}/unsubscribe`, { email: email.trim() });
      if (res.data.status === "unsubscribed") {
        setState("success");
        setMessage("You've been unsubscribed. No more update emails will be sent to this address — thanks for following the journey.");
      } else {
        setState("error");
        setMessage("We couldn't find that email address on our update list.");
      }
    } catch {
      setState("error");
      setMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <div data-testid="unsubscribe-page">
      <Header />
      <main className="bg-[#F7F1E6] pb-24 pt-32 sm:pt-36">
        <div className="mx-auto max-w-xl px-4 sm:px-6">
          <h1 className="text-[2rem] font-extrabold tracking-tight text-[#074345] sm:text-4xl">
            Unsubscribe from updates
          </h1>
          <p className="mt-4 text-base leading-relaxed text-[#243C39]">
            Enter the email address you signed up with and we&rsquo;ll remove it from the
            Peanut Valet update list.
          </p>

          {state === "success" ? (
            <div
              data-testid="unsubscribe-success-message"
              role="status"
              className="mt-8 flex items-start gap-4 rounded-[2rem] bg-[#EADCC6] p-7"
            >
              <CheckCircle2 className="mt-0.5 h-7 w-7 shrink-0 text-[#F3802A]" aria-hidden="true" />
              <p className="text-base text-[#243C39]">{message}</p>
            </div>
          ) : (
            <form data-testid="unsubscribe-form" onSubmit={submit} noValidate className="mt-8 rounded-[2rem] bg-[#EADCC6] p-6 sm:p-8">
              <label htmlFor="unsubscribe-email" className="mb-2 block text-sm font-bold text-[#074345]">
                Email address <span className="text-[#F3802A]">*</span>
              </label>
              <input
                id="unsubscribe-email"
                data-testid="unsubscribe-email-input"
                type="email"
                required
                aria-required="true"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                aria-invalid={state === "error" ? "true" : undefined}
                aria-describedby={state === "error" ? "unsubscribe-error" : undefined}
                className="w-full rounded-full border-2 border-[#F7F1E6] bg-white px-5 py-3.5 text-base text-[#243C39] placeholder-[#506460] transition-colors focus:border-[#F3802A] focus:outline-none"
              />
              {state === "error" && (
                <p id="unsubscribe-error" data-testid="unsubscribe-error-message" role="alert" className="mt-3 text-sm font-bold text-[#B3400F]">
                  {message}
                </p>
              )}
              <button
                type="submit"
                data-testid="unsubscribe-submit-button"
                disabled={state === "submitting"}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#F3802A] px-8 py-4 text-base font-extrabold text-[#043436] transition-[transform,filter] duration-200 hover:scale-[1.02] hover:brightness-95 active:scale-[0.98] disabled:opacity-70"
              >
                {state === "submitting" && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                Unsubscribe
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
